import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  /** ms before auto-dismiss; 0 = no auto-dismiss */
  duration: number;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let _idCounter = 0;
function nextId(): string {
  return String(++_idCounter);
}

const STYLE_MAP: Record<ToastType, { bg: string; color: string; icon: string; border: string }> = {
  success: { bg: '#f0fdf4', color: '#166534', icon: 'bi-check-circle-fill', border: '#86efac' },
  error:   { bg: '#fef2f2', color: '#991b1b', icon: 'bi-x-circle-fill',     border: '#fca5a5' },
  warning: { bg: '#fffbeb', color: '#92400e', icon: 'bi-exclamation-triangle-fill', border: '#fcd34d' },
  info:    { bg: '#eff6ff', color: '#1e40af', icon: 'bi-info-circle-fill',   border: '#93c5fd' },
};

function SingleToast({
  item,
  onClose,
}: {
  item: ToastItem;
  onClose: (id: string) => void;
}) {
  const s = STYLE_MAP[item.type];
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    setExiting(true);
    timerRef.current = setTimeout(() => onClose(item.id), 280);
  }, [item.id, onClose]);

  useEffect(() => {
    if (item.duration > 0) {
      const t = setTimeout(dismiss, item.duration);
      return () => clearTimeout(t);
    }
  }, [item.duration, dismiss]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        minWidth: 280,
        maxWidth: 420,
        padding: '10px 14px',
        borderRadius: 8,
        background: s.bg,
        border: `1px solid ${s.border}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        color: s.color,
        fontSize: '0.875rem',
        fontWeight: 500,
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateX(20px)' : 'translateX(0)',
        transition: 'opacity 0.28s ease, transform 0.28s ease',
      }}
    >
      <i className={`bi ${s.icon}`} style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }} />
      <span style={{ flex: 1, lineHeight: 1.4 }}>{item.message}</span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: s.color,
          opacity: 0.6,
          fontSize: '0.85rem',
          lineHeight: 1,
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback(
    (message: string, type: ToastType = 'success', duration = 4500) => {
      setToasts((prev) => [...prev, { id: nextId(), message, type, duration }]);
    },
    [],
  );

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast stack — top-right, below the 56px navbar */}
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 68,
          right: 20,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <SingleToast item={t} onClose={remove} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
